import api from '@/lib/api';
import { getSystemId } from '@/lib/deviceId';
import { encryptString, decryptString } from '@/lib/secureStore';
import { getClientIp, getDeviceType } from '@/lib/clientInfo';
import {
  saveSessionCredentials,
  clearSessionCredentials,
  getSessionCredentials,
} from '@/lib/credentialsStore';

const ENDPOINTS = {
  login: '/login.aspx',
  logout: '/logout.aspx',
  forgotPassword: '/forgotpassword.aspx',
  dbCheckUpdate: '/dbcheckupdateupdate.aspx',
};

export const AUTH_REMEMBER_KEY = 'chanakya.auth.remember';
const SESSION_KEY = 'chanakya.session';
const REMEMBERED_KEY = 'chanakya.auth.remembered';
const LEGACY_KEYS = ['chanakya.auth.user', 'chanakya.auth.token'];

/**
 * Sign in against the CHANAKYA ERP login API.
 * Sends { licenceid, licencepwd, username, password, systemid, clientip, devicetype }.
 * clientip is best-effort — an empty string is sent if the public-IP lookup
 * fails or times out. devicetype is derived synchronously from the UA.
 */
export async function login(payload) {
  const [clientip, devicetype] = [await getClientIp(), getDeviceType()];
  const body = {
    licenceid: payload.licenceid,
    licencepwd: payload.licencepwd,
    username: payload.username,
    password: payload.password,
    systemid: getSystemId(),
    clientip,
    devicetype,
  };
  const { data } = await api.post(ENDPOINTS.login, body);
  return {
    success: Boolean(data?.success),
    message: data?.message || '',
    data: data?.success && data?.data ? data.data : null,
    raw: data,
  };
}

export async function forgotPassword(payload) {
  const { data } = await api.post(ENDPOINTS.forgotPassword, {
    ...payload,
    systemid: getSystemId(),
  });
  return data;
}

/**
 * Split the login API's `data` block into the shapes our contexts expect.
 */
export function normaliseLoginData(data) {
  if (!data) return { user: null, companies: [], fys: [], permissioncode: [], erpversion: '', dbupdaterequired: false };
  const {
    loginname,
    mobileno,
    emailid,
    cloudconnection,
    salesmanrefno,
    erpversion = '',
    dbupdaterequired = false,
    company = [],
    fy = [],
    permissioncode = [],
  } = data;
  return {
    user: { loginname, mobileno, emailid, cloudconnection, salesmanrefno },
    companies: Array.isArray(company) ? company : [],
    fys: Array.isArray(fy) ? fy : [],
    permissioncode: Array.isArray(permissioncode)
      ? permissioncode
          .map((p) => (p && typeof p === 'object' ? p.code : p))
          .filter((c) => c != null)
          .map(String)
      : [],
    erpversion: String(erpversion || ''),
    dbupdaterequired: Boolean(dbupdaterequired),
  };
}

/**
 * Trigger the database check/update workflow. Called from the login
 * flow when the server flags `dbupdaterequired = true`. Sends the
 * same envelope every other action endpoint uses so the request
 * carries a proper session identity.
 */
export async function dbCheckUpdate(creds) {
  const [clientip, devicetype] = [await getClientIp(), getDeviceType()];
  const body = {
    licenceid:  creds.licenceid,
    username:   creds.username,
    password:   creds.password,
    systemid:   getSystemId(),
    clientip,
    devicetype,
    cloudconn:  creds.cloudconn || creds.cloudconnection || '',
  };
  // `actionApi: false` — this call ITSELF is an action but we don't
  // want the Central Action-API Error Modal to appear on failure: the
  // login-page flow shows a bespoke DB-update failure UI instead.
  const { data } = await api.post(ENDPOINTS.dbCheckUpdate, body, { actionApi: false });
  return {
    success: Boolean(data?.success),
    message: String(data?.message || ''),
  };
}

/**
 * Persist session (user + companies + fys + permissioncode) and — always —
 * the current login credentials in an AES-GCM-encrypted **sessionStorage**
 * copy so background API calls (KPI, last-activity, etc.) can retrieve
 * them. If Remember Me is checked we additionally persist a decryptable
 * **localStorage** copy so the credentials survive a browser restart.
 */
export async function persistSession({
  user,
  companies,
  fys,
  permissioncode = [],
  erpversion = '',
  rememberMe,
  credentials,
}) {
  const payload = JSON.stringify({ user, companies, fys, permissioncode, erpversion });
  const primary = rememberMe ? localStorage : sessionStorage;
  const secondary = rememberMe ? sessionStorage : localStorage;
  primary.setItem(SESSION_KEY, payload);
  secondary.removeItem(SESSION_KEY);
  localStorage.setItem(AUTH_REMEMBER_KEY, rememberMe ? '1' : '0');

  // Always keep an encrypted session copy of the credentials so background
  // API calls have what they need. When Remember Me is on we ALSO mirror
  // the copy to localStorage so it survives a browser restart.
  if (credentials) {
    await saveSessionCredentials(credentials, { persist: Boolean(rememberMe) });
  }

  if (rememberMe && credentials) {
    const licencepwd_enc = await encryptString(credentials.licencepwd);
    const password_enc = await encryptString(credentials.password);
    localStorage.setItem(
      REMEMBERED_KEY,
      JSON.stringify({
        licenceid: credentials.licenceid,
        username: credentials.username,
        licencepwd_enc,
        password_enc,
      }),
    );
  } else {
    localStorage.removeItem(REMEMBERED_KEY);
  }
}

export function getSession() {
  const raw =
    localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getCurrentUser() {
  return getSession()?.user || null;
}

/**
 * Return the current user's permission codes as an array of strings.
 * The list is populated by the login API and persisted in sessionStorage
 * (or localStorage when Remember Me is checked). An empty array means the
 * user has no permissions yet (or the session hasn't been established).
 *
 * The special sentinel value "0" indicates an administrator with access to
 * every menu / feature.
 */
export function getPermissionCodes() {
  const raw = getSession()?.permissioncode;
  if (!Array.isArray(raw)) return [];
  return raw;
}

/**
 * Load and decrypt remembered credentials.
 */
export async function getRememberedCredentials() {
  const raw = localStorage.getItem(REMEMBERED_KEY);
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const [licencepwd, password] = await Promise.all([
    decryptString(parsed.licencepwd_enc),
    decryptString(parsed.password_enc),
  ]);
  if (licencepwd == null || password == null) return null;
  return {
    licenceid: parsed.licenceid || '',
    username: parsed.username || '',
    licencepwd,
    password,
  };
}

/**
 * Fire-and-forget "user signed out" ping to the report server. We
 * capture the credentials BEFORE wiping local session state, then
 * dispatch the request without awaiting — the sign-out flow must
 * succeed even if the network / server never responds.
 */
async function fireLogoutPing() {
  try {
    const creds = await getSessionCredentials();
    if (!creds?.licenceid || !creds?.username) return;
    const clientip = await getClientIp().catch(() => '');
    // Don't await — the caller has already cleared the UI. Any error
    // (offline, 5xx, timeout) is intentionally swallowed.
    api.post(ENDPOINTS.logout, {
      licenceid:  creds.licenceid,
      username:   creds.username,
      password:   creds.password,
      systemid:   getSystemId(),
      clientip,
      devicetype: getDeviceType(),
    }, { timeout: 5000 }).catch(() => { /* swallow */ });
  } catch { /* swallow — signout must always succeed */ }
}

export function clearSession() {
  // Ping the server FIRST while we still have creds — non-blocking.
  fireLogoutPing();
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(REMEMBERED_KEY);
  clearSessionCredentials();
  LEGACY_KEYS.forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}
